<template>
  <div>
    <div class="Content Integrations">
      <main class="SectionContainer u-flex">
        <section :class="[{'is-loading': ui.isLoading} , 'SectionContainer-left']">
          <h3>Integration</h3>
          <ul>
            <li
              v-for="integrationListItem in integrationsList"
              :key="integrationListItem.id"
              class="u-flex IntegrationsListItem"
              :class="{'Selected': ui.selectedItemName === integrationListItem.name}"
              @click="handleSelectIntegration(integrationListItem)"
            >
              <figure>
                <img
                  :src="integrationListItem.logo"
                  alt=""
                >
              </figure>
              <div
                class="u-flex-grow"
              >
                <h4>{{ integrationListItem.name }}</h4>
                <p class="hint">
                  {{ integrationListItem.description }}
                </p>
              </div>
              <button
                v-if="!event.integrations.find(i => i.name === integrationListItem.name)"
                class="Button Button--transparent IntegrationConnectButton"
                @click="handleCreateNewIntegration(integrationListItem)"
              >
                Connect
              </button>
            </li>
          </ul>
        </section>
        <section class="SectionContainer-right">
          <p
            v-if="currentView === views.noSelection"
            class="NoIntegrationMessage"
          >
            Select or Connect to an integration
          </p>
          <template v-if="isDeliveryListView && currentIntegration">
            <div>
              <div class="IntegrationActions u-flex">
                <rmp-toggle
                  v-model="currentIntegration.is_paused"
                  :checked="!currentIntegration.is_paused"
                  class="IntegrationToggle"
                  label="Integration on"
                  @change="handleTogglePauseIntegration"
                />
                <button
                  v-if="isZapierIntegration"
                  class="Button Button--transparent"
                  :disabled="totalRecords === 0"
                  @click="showResendZapierDataModal"
                >
                  Send Data
                </button>
                <button
                  class="Button Button--transparent"
                  @click="handleEditIntegration"
                >
                  <span
                    class="Icon Icon-pencil"
                  /> Edit
                </button>
                <button
                  :class="{'is-loading': ui.deleteButtonLoading}"
                  class="Button Button--transparent"
                  @click="handleDeleteIntegration"
                >
                  <span class="Icon Icon-trash" /> Delete
                </button>
              </div>
              <div>
                <h3>{{ currentIntegration.name }}</h3>
                <div
                  v-if="ui.integrationCreatedOrEdited && totalRecords > 0"
                  class="Notice Notice--info no-margin"
                >
                  <div
                    class="Notice-copy u-flex u-flex-alignCenter u-flex-spaceBetween"
                  >
                    <div>
                      <p>
                        <span class="Icon-s Icon-exclamation-triangle" />Integration was created to an existing form with records, click Send Data to sync
                      </p>
                    </div>
                    <button
                      class="Button Button--green"
                      @click="showResendZapierDataModal"
                    >
                      Send Data
                    </button>
                  </div>
                </div>
                <deliveries-info
                  v-if="selectedDelivery"
                  :delivery="selectedDelivery"
                  @deliveries:close="handleSelectedDelivery"
                />

                <integration-deliveries-data-table
                  v-show="!selectedDelivery"
                  :key="currentIntegration.id"
                  :event-id="event.id"
                  :integration="currentIntegration"
                  @integration-deliveries:show="handleSelectedDelivery"
                />
              </div>
            </div>
          </template>
          <question-mapping
            v-if="isQuestionMappingView && newIntegration"
            :key="newIntegration.name"
            v-model="newIntegration"
            :integration="newIntegration"
            :event-questions="questionsCompatibleWithIntegrations()"
            @question-mapping:submit="handleQuestionMappingSubmit"
            @question-mapping:cancel="handleCancelQuestionMapping"
          />

          <conditional-logic
            v-if="isConditionalLogicView"
            :integration="newIntegration"
            @conditional-logic:back-navigation="currentView = views.mapQuestions"
            @conditional-logic:submit="handleConditionalLogicSubmit"
          />
          <zapier-options
            v-if="isZapierConfigView"
            :key="newIntegration.name"
            :integration="newIntegration"
            @zapier-options:submit="handleZapierOptionsSubmit"
            @zapier-options:back-navigation="currentView = views.conditionalLogic"
          />
        </section>
        <delete-integration-modal
          v-model="ui.showDeleteIntegrationModal"
          :event-id="event.id"
          :integration="currentIntegration"
          @delete-integration-modal:success="handleDeleteIntegration"
        />
        <resend-zapier-data-modal
          v-model="ui.showResendZapierDataModal"
          :zapier-delivery-total="totalRecords"
          @resend-zapier-data:confirmation="handleSendData"
        />
      </main>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import RmpToggle from '@rmpenterprise/form-toggle';
import QuestionMapping from './integration-views/question-mapping.vue';
import ConditionalLogic from './integration-views/conditional-logic.vue';
import ZapierOptions from './integration-views/zapier-options.vue';
import IntegrationDeliveriesDataTable from '../data-tables/integration-deliveries-data-table.vue';
import DeliveriesInfo from './integration-views/deliveries.vue';
import DeleteIntegrationModal from '../modals/delete-integration.vue';
import '@rmpenterprise/form-toggle/dist/toggle.esm.css';
import ResendZapierDataModal from '../modals/resend-zapier-data.vue';

export default {
  components: {
    ResendZapierDataModal,
    QuestionMapping,
    ConditionalLogic,
    ZapierOptions,
    IntegrationDeliveriesDataTable,
    RmpToggle,
    DeliveriesInfo,
    DeleteIntegrationModal,
  },
  data() {
    return {
      newIntegration: null,
      currentIntegration: null,
      mappedQuestions: [],
      conditionalLogic: {},
      selectedDelivery: null,
      views: {
        noSelection: 'noSelection',
        deliveriesList: 'deliveriesList',
        mapQuestions: 'mapQuestions',
        conditionalLogic: 'conditionalLogic',
        zapierConfig: 'zapierConfig',
      },
      zapierOptions: {
        sharedSecret: null,
        endpoint: null,
      },
      currentView: 'noSelection',
      ui: {
        isLoading: false,
        deleteButtonLoading: false,
        submitLoading: false,
        selectedItemName: null,
        showDeleteIntegrationModal: false,
        integrationCreatedOrEdited: false,
        showResendZapierDataModal: false,
      },
    };
  },
  computed: {
    ...mapState('events', {
      event: 'event',
      integrationsList: (state) => state.lists.integrations,
    }),
    isZapierIntegration() {
      return this.currentIntegration ? this.currentIntegration.web_forms_integration_type_uuid === 'zapier' : this.newIntegration.uuid === 'zapier';
    },
    isQuestionMappingView() {
      return this.currentView === this.views.mapQuestions;
    },
    isConditionalLogicView() {
      return this.currentView === this.views.conditionalLogic;
    },
    isZapierConfigView() {
      return this.currentView === this.views.zapierConfig;
    },
    isDeliveryListView() {
      return this.currentView === this.views.deliveriesList;
    },
    totalRecords() {
      return this.event.total_records;
    },
  },
  mounted() {
    if (this.$route.params.eventId !== undefined) {
      this.ui.isLoading = true;

      this.getIntegrationsList({ eventId: this.event.id })
        .then(() => {
          this.ui.isLoading = false;
        })
        .catch(({ data }) => {
          this.ui.isLoading = false;
          this.$bus.$emit('displayMsg', {
            text: data.message,
            type: 'warning',

          });
        });
    }
  },
  methods: {
    ...mapActions(
      'events',
      ['getEvent',
        'updateEvent',
        'getIntegrationsList',
        'createIntegration',
        'updateIntegration',
        'getIntegration',
        'togglePauseIntegration',
        'deleteIntegration',
        'sendData',
      ],
    ),
    questionsCompatibleWithIntegrations() {
      return this.event.questions.filter((question) => {
        const typeOfQuestionsIncompatibleWithIntegrations = ['statement', 'paragraph'];
        return !typeOfQuestionsIncompatibleWithIntegrations.includes(question.type);
      });
    },
    handleQuestionMappingSubmit(data) {
      this.mappedQuestions = data;
      this.newIntegration.questions_mapping = data;
      this.setCurrentView(this.views.conditionalLogic);
    },
    handleConditionalLogicSubmit(data) {
      this.conditionalLogic = data;
      this.newIntegration.conditional_delivery_criteria = data;

      if (this.isZapierIntegration) {
        this.setCurrentView(this.views.zapierConfig);
      } else {
        this.handleSubmitIntegration();
      }
    },
    handleZapierOptionsSubmit(data) {
      this.zapierOptions = {
        sharedSecret: data.sharedSecret,
        endpoint: data.endpoint,
      };

      this.handleSubmitIntegration();
    },
    handleSelectIntegration(integrationListItem) {
      const existingIntegration = this.event.integrations.find((i) => i.name === integrationListItem.name);

      if (existingIntegration) {
        this.ui.selectedItemName = existingIntegration.name;

        this.getIntegrationById(existingIntegration.id).then((data) => {
          this.currentIntegration = data.meta.integration;
          this.setCurrentView(this.views.deliveriesList);
        });
      }
    },
    handleSelectedDelivery(delivery = null) {
      this.selectedDelivery = delivery;
    },
    getIntegrationById(integrationId) {
      return this.getIntegration({ eventId: this.event.id, integrationId });
    },
    handleCreateNewIntegration(integration) {
      this.ui.selectedItemName = integration.name;
      this.setCurrentView(this.views.mapQuestions);
      this.currentIntegration = null;
      this.newIntegration = integration;
      this.ui.integrationCreatedOrEdited = true;
    },
    handleEditIntegration() {
      this.setCurrentView(this.views.mapQuestions);
      this.newIntegration = this.currentIntegration;
      this.ui.integrationCreatedOrEdited = true;
    },
    handleCancelQuestionMapping() {
      if (this.currentIntegration) {
        this.setCurrentView(this.views.deliveriesList);
      } else {
        this.setCurrentView(this.views.noSelection);
        this.ui.selectedItemName = null;
      }
      this.newIntegration = null;
    },
    setCurrentView(view) {
      this.currentView = view;
    },
    handleDeleteIntegration() {
      this.ui.showDeleteIntegrationModal = true;
      this.ui.selectedItemName = null;
      this.setCurrentView(this.views.noSelection);
    },
    handleTogglePauseIntegration() {
      this.$nextTick(() => {
        this.togglePauseIntegration({
          eventId: this.event.id,
          integrationId: this.currentIntegration.id,
          paused: this.currentIntegration.is_paused,
        })
          .then(({ data }) => {
            this.ui.submitLoading = false;
            this.$bus.$emit('displayMsg', {
              text: data.message,
              type: 'success',
            });
          });
      });
    },
    showResendZapierDataModal() {
      this.ui.showResendZapierDataModal = true;
    },
    handleSendData() {
      this.sendData({ eventId: this.event.id, integrationId: this.currentIntegration.id })
        .then((data) => {
          this.ui.integrationCreatedOrEdited = false;
          this.$bus.$emit('displayMsg', {
            text: `${data.message}. Records - ${this.totalRecords}. `,
            type: 'success',
            duration: 6000,
          });
          this.ui.showResendZapierDataModal = false;
        })
        .catch(({ data }) => {
          this.ui.submitLoading = false;
          this.ui.showResendZapierDataModal = false;
          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'warning',
            duration: 6000,
          });
        });
    },
    updateExistingIntegration(payload) {
      this.updateIntegration({ eventId: this.event.id, integrationId: this.currentIntegration.id, payload })
        .then((data) => {
          this.getIntegrationById(this.newIntegration.id)
            .then((response) => {
              this.ui.submitLoading = false;
              this.currentIntegration = response.meta.integration;
              this.setCurrentView(this.views.deliveriesList);
            });
          this.$bus.$emit('displayMsg', {
            text: data.message,
            type: 'success',
            duration: 5000,
          });
        })
        .catch(({ data }) => {
          this.ui.submitLoading = false;

          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'warning',
            duration: 5000,
          });
        });
    },
    createNewIntegration(payload) {
      this.createIntegration({ eventId: this.event.id, payload })
        .then((data) => {
          const [existingIntegration] = data.data.filter((integration) => integration.webFormsIntegrationTypeUuid === this.newIntegration.uuid);

          // Now load the integration data into the UI
          this.getIntegrationById(existingIntegration.id)
            .then((response) => {
              this.ui.submitLoading = false;
              this.currentIntegration = response.meta.integration;
              this.setCurrentView(this.views.deliveriesList);
            });

          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'success',
            duration: 5000,
          });
        })
        .catch(({ data }) => {
          if (data) {
            this.$bus.$emit('displayMsg', {
              text: data.data.message,
              type: 'warning',
              duration: 5000,
            });
          }
          this.ui.submitLoading = false;
        });
    },
    handleSubmitIntegration() {
      this.ui.submitLoading = true;

      const conditionalDeliveryCriteria = this.conditionalLogic.constraint === null
      && this.conditionalLogic.html_form_input_id === null
      && (this.conditionalLogic.value === null || this.conditionalLogic.value === '')
        ? null
        : this.conditionalLogic;

      const payload = {
        web_forms_integration_type_uuid: this.newIntegration.uuid,
        questions_mapping: this.mappedQuestions,
        conditional_delivery_criteria: conditionalDeliveryCriteria,
      };

      if (this.isZapierIntegration) {
        payload.shared_secret = this.zapierOptions.sharedSecret;
        payload.endpoint = this.zapierOptions.endpoint;
      }

      // Editing an existing integration
      if (this.newIntegration.id) {
        this.updateExistingIntegration(payload);
      } else {
        this.createNewIntegration(payload);
      }
    },
  },
};
</script>

<style lang="less" scoped>

.Integrations {
  .SectionContainer-left {
    flex-basis: 30%;
  }

  .IntegrationConnectButton {
    height: 40px;
    align-self: center;
  }

  .SectionContainer-right {
    flex-grow: 1;
    padding: 12px;
    border-left: 1px solid lightgray;

    .NoIntegrationMessage {
      text-align: center;
      top: 50%;
      position: relative;
    }
  }
}

.IntegrationsListItem {
  border: 1px solid lightgrey;
  padding: 10px;
  margin: 10px 0;
  border-radius: 6px;
  min-width: 270px;

  &.Selected {
    border: 1px solid #046FF0;
  }

  p, h4 {
    margin: 0;
  }
}

.Divider {
  height: 1px;
  position: relative;
  width: 101px;
  background: #A7A7A7;
  align-self: center;
  margin: 0 10px;

  .Icon {
    position: absolute;
    color: #A7A7A7;
    top: -9px;
    right: 15px;
    font-size: 1.4rem;
  }
}

.IntegrationActions {
  align-items: center;
  justify-content: flex-end;

  .IntegrationToggle {
    margin-bottom: 0;
    width: 190px;
  }
}

.MappingQuestionsContainer {
  border: 1px solid #E3E3E3;
  border-radius: 8px;
  background-color: #FFFFFF;
  box-shadow: 0 13px 29px 0 rgba(0, 0, 0, 0.11);
  padding-bottom: 25px;

  .Heading {
    padding: 21px 30px;
    border-bottom: 1px solid lightgray;
    margin: 0;
  }

  .MappingContainer {
    .Headers {

      h3 {
        width: 100%;

        &:first-child {
          margin-right: 55px;
        }
      }

    }

    .MappingQuestions {
      margin: 15px 0;
      align-items: center;

      .Icon-times {
        margin-left: 10px;
        cursor: pointer;
      }
    }
  }
}

.Logic {
  background-color: #f7f7f7;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  padding: 30px;
}

.Footer {
  margin-top: 40px;
}

.Icon-s.Icon-exclamation-triangle {
  margin-right: 10px;
}

</style>
